import type { ClaudeSession, ConversationTurn, ConversationTurnEntry } from '@/index'
import { AgentMode, EventType, HookType } from '@/index'
import type {
  FormattedAgentActivity,
  FormattedUserActivity,
} from '@/features/events/activity/utils/types'
import type { NotificationContext, FileChanges } from '@/infra/sse/conversation/payloads'
import type { TmuxSession, UserHostDetails } from '@/infra/sse/shared/payloads'
import {
  createClaudeSessionRuntime,
  buildSessionBreadcrumb,
} from '@/features/events/conversation/state/types'

// ---------------------------------------------------------------------------
// Deterministic IDs
// ---------------------------------------------------------------------------

const USER_EXTERNAL_ID = '00000000-0000-0000-0000-000000000000'

const SESSION_IDS = {
  S1: '00000000-0000-0000-0000-000000000001',
  S2: '00000000-0000-0000-0000-000000000002',
  S3: '00000000-0000-0000-0000-000000000003',
  S4: '00000000-0000-0000-0000-000000000004',
  S5: '00000000-0000-0000-0000-000000000005',
  S6: '00000000-0000-0000-0000-000000000006',
  S7: '00000000-0000-0000-0000-000000000007',
  S8: '00000000-0000-0000-0000-000000000008',
  S9: '00000000-0000-0000-0000-000000000009',
  S10: '00000000-0000-0000-0000-000000000010',
  S11: '00000000-0000-0000-0000-000000000011',
  S12: '00000000-0000-0000-0000-000000000012',
  S13: '00000000-0000-0000-0000-000000000013',
  S14: '00000000-0000-0000-0000-000000000014',
  S15: '00000000-0000-0000-0000-000000000015',
  S16: '00000000-0000-0000-0000-000000000016',
} as const

const HOST_IDS = {
  MACBOOK_PRO: '10000000-0000-0000-0000-000000000001',
  DEV_SERVER: '10000000-0000-0000-0000-000000000002',
  CI_RUNNER: '10000000-0000-0000-0000-000000000003',
} as const

const TMUX_PANE_ID = '20000000-0000-0000-0000-000000000001'

// ---------------------------------------------------------------------------
// Timestamp helpers
// ---------------------------------------------------------------------------

const BASE_TIMESTAMP = new Date('2025-01-15T14:30:00Z').getTime()

/** Create a timestamp N minutes before the base time. Higher = older = appears lower in 'newest' sort. */
function minutesAgo(minutes: number): string {
  return new Date(BASE_TIMESTAMP - minutes * 60_000).toISOString()
}

// ---------------------------------------------------------------------------
// Host definitions
// ---------------------------------------------------------------------------

const HOST_MACBOOK_PRO: UserHostDetails = {
  hostId: HOST_IDS.MACBOOK_PRO,
  hostName: 'Macbook Pro',
}

const HOST_DEV_SERVER: UserHostDetails = {
  hostId: HOST_IDS.DEV_SERVER,
  hostName: 'Dev Server',
}

const HOST_CI_RUNNER: UserHostDetails = {
  hostId: HOST_IDS.CI_RUNNER,
  hostName: 'CI Runner',
}

// ---------------------------------------------------------------------------
// Event builder
// ---------------------------------------------------------------------------

interface MockEventConfig {
  readonly eventType: EventType
  readonly agentMode: AgentMode
  readonly notificationMessage?: string
  readonly agentResponse?: string
  readonly userPrompt?: string
  readonly toolName?: string
  readonly notificationContext?: NotificationContext | null
  readonly fileChanges?: FileChanges | null
  readonly timestamp?: string
}

export function buildMockEvent(config: MockEventConfig): ConversationTurnEntry {
  return {
    eventId: crypto.randomUUID(),
    timestamp: config.timestamp ?? new Date().toISOString(),
    hookType: resolveHookType(config.eventType),
    eventType: config.eventType,
    notificationMessage: config.notificationMessage ?? null,
    toolName: config.toolName ?? null,
    notificationContext: config.notificationContext ?? null,
    agentMode: config.agentMode,
    agentResponse: config.agentResponse ?? null,
    fileChanges: config.fileChanges ?? null,
    userPrompt: config.userPrompt ?? null,
    encryption: null,
  }
}

function resolveHookType(eventType: EventType): HookType | null {
  switch (eventType) {
    case EventType.FILE_MODIFICATION:
    case EventType.AGENT_NARRATIVE:
      return HookType.NOTIFICATION
    case EventType.TOOL_PERMISSION_REQUEST:
    case EventType.AGENT_QUESTIONS:
    case EventType.PLAN_APPROVAL_REQUEST:
      return HookType.STOP
    case EventType.AGENT_REPLY:
      return HookType.NOTIFICATION
    default:
      return null
  }
}

// ---------------------------------------------------------------------------
// Session builder
// ---------------------------------------------------------------------------

interface MockSessionConfig {
  readonly sessionId: string
  readonly sessionName: string
  readonly hostName: string
  readonly host: UserHostDetails
  readonly contextWorkDirectory: string
  readonly tmuxSessionName: string | null
  readonly dangerouslySkipPermissions: boolean
  readonly currentAgentMode: AgentMode
  readonly latestEvent: ConversationTurnEntry
  readonly isProcessing: boolean
}

function buildTmuxSession(tmuxSessionName: string | null): TmuxSession | null {
  if (tmuxSessionName === null) return null
  return {
    sessionId: crypto.randomUUID(),
    sessionName: tmuxSessionName,
    paneId: TMUX_PANE_ID,
  }
}

function buildMockSession(config: MockSessionConfig): ClaudeSession {
  const turnId = crypto.randomUUID()
  const conversationTurns = new Map<string, ConversationTurn>()
  conversationTurns.set(turnId, {
    userPromptId: turnId,
    startTime: config.latestEvent.timestamp,
    endTime: null,
    events: [config.latestEvent],
    userCommands: [],
  })

  const sessionBreadcrumb = buildSessionBreadcrumb(
    config.sessionName,
    config.hostName,
    config.contextWorkDirectory,
    config.sessionId,
  )

  const runtime = createClaudeSessionRuntime()

  return {
    sessionId: config.sessionId,
    userExternalId: USER_EXTERNAL_ID,
    transcriptPath: `/home/dev/.claude/transcripts/${config.sessionId}.json`,
    contextWorkDirectory: config.contextWorkDirectory,
    tmuxSession: buildTmuxSession(config.tmuxSessionName),
    userHostDetails: config.host,
    conversationTurns,
    sessionName: config.sessionName,
    sessionBreadcrumb,
    dangerouslySkipPermissions: config.dangerouslySkipPermissions,
    autoModeSupported: false,
    currentAgentMode: config.currentAgentMode,
    runtime,
    isTransient: false,
  }
}

// ---------------------------------------------------------------------------
// Helper: build a minimal NotificationContext for tool events
// ---------------------------------------------------------------------------

export function buildToolNotificationContext(toolName: string): NotificationContext {
  return {
    toolName,
    toolInput: { file_path: '' },
    agentDialogue: null,
  }
}

function buildAskUserQuestionContext(): NotificationContext {
  return {
    toolName: 'AskUserQuestion',
    toolInput: {
      questions: [
        {
          question: 'Which validation library do you prefer?',
          header: 'Validation Library',
          multiSelect: false,
          options: [
            { label: 'Zod', description: 'TypeScript-first schema validation' },
            { label: 'Yup', description: 'Object schema validation' },
            { label: 'Custom', description: 'Implement custom validation' },
          ],
        },
      ],
    },
    agentDialogue: {
      agentPrompt: 'Claude Code needs your attention',
      userOptions: [
        { index: 0, option: 'Zod' },
        { index: 1, option: 'Yup' },
        { index: 2, option: 'Custom validation' },
      ],
    },
  }
}

function buildBashNotificationContext(command: string): NotificationContext {
  return {
    toolName: 'Bash',
    toolInput: { command },
    agentDialogue: null,
  }
}

// ===========================================================================
//  macbook-pro (expanded, 9 sessions)
// ===========================================================================

// ---------------------------------------------------------------------------
// Nexus Web — /home/dev/projects/nexus-web (5 sessions: S1–S5)
// ---------------------------------------------------------------------------

// S1: Implement OAuth2 — LIVE animated, FILE_MODIFICATION
export const SESSION_IMPLEMENT_OAUTH2 = buildMockSession({
  sessionId: SESSION_IDS.S1,
  sessionName: 'Implement OAuth2',
  hostName: 'Macbook Pro',
  host: HOST_MACBOOK_PRO,
  contextWorkDirectory: '/home/dev/projects/nexus-web',
  tmuxSessionName: 'implement-oauth2',
  dangerouslySkipPermissions: false,
  currentAgentMode: AgentMode.DEFAULT_MODE,
  isProcessing: true,
  latestEvent: buildMockEvent({
    eventType: EventType.FILE_MODIFICATION,
    agentMode: AgentMode.DEFAULT_MODE,
    notificationMessage: 'File modified: OAuthCallback.tsx',
    toolName: 'Write',
    notificationContext: buildToolNotificationContext('Write'),
    timestamp: minutesAgo(0),
  }),
})

// S2: Add Form Validation — Blocking (pulsing circle), AGENT_QUESTIONS
export const SESSION_ADD_FORM_VALIDATION = buildMockSession({
  sessionId: SESSION_IDS.S2,
  sessionName: 'Add Form Validation',
  hostName: 'Macbook Pro',
  host: HOST_MACBOOK_PRO,
  contextWorkDirectory: '/home/dev/projects/nexus-web',
  tmuxSessionName: 'add-form-validation',
  dangerouslySkipPermissions: false,
  currentAgentMode: AgentMode.AUTO_ACCEPT_EDITS_MODE,
  isProcessing: false,
  latestEvent: buildMockEvent({
    eventType: EventType.AGENT_QUESTIONS,
    agentMode: AgentMode.AUTO_ACCEPT_EDITS_MODE,
    notificationMessage: 'Claude Code needs your attention',
    toolName: 'AskUserQuestion',
    notificationContext: buildAskUserQuestionContext(),
    timestamp: minutesAgo(5),
  }),
})

// S3: Fix Rate Limiter — Blocking (pulsing triangle, red border, red diamond), TOOL_PERMISSION_REQUEST
export const SESSION_FIX_RATE_LIMITER = buildMockSession({
  sessionId: SESSION_IDS.S3,
  sessionName: 'Fix Rate Limiter',
  hostName: 'Macbook Pro',
  host: HOST_MACBOOK_PRO,
  contextWorkDirectory: '/home/dev/projects/nexus-web',
  tmuxSessionName: 'fix-rate-limiter',
  dangerouslySkipPermissions: true,
  currentAgentMode: AgentMode.BYPASS_PERMISSIONS_MODE,
  isProcessing: false,
  latestEvent: buildMockEvent({
    eventType: EventType.TOOL_PERMISSION_REQUEST,
    agentMode: AgentMode.BYPASS_PERMISSIONS_MODE,
    notificationMessage: 'Claude wants to run: docker compose down && docker compose up -d --build',
    toolName: 'Bash',
    notificationContext: buildBashNotificationContext(
      'docker compose down && docker compose up -d --build',
    ),
    timestamp: minutesAgo(20),
  }),
})

// S4: Optimize Bundle Size — Blocking (pulsing square, cyan diamond), PLAN_APPROVAL_REQUEST
export const SESSION_OPTIMIZE_BUNDLE_SIZE = buildMockSession({
  sessionId: SESSION_IDS.S4,
  sessionName: 'Optimize Bundle Size',
  hostName: 'Macbook Pro',
  host: HOST_MACBOOK_PRO,
  contextWorkDirectory: '/home/dev/projects/nexus-web',
  tmuxSessionName: 'optimize-bundle-size',
  dangerouslySkipPermissions: false,
  currentAgentMode: AgentMode.PLAN_MODE,
  isProcessing: false,
  latestEvent: buildMockEvent({
    eventType: EventType.PLAN_APPROVAL_REQUEST,
    agentMode: AgentMode.PLAN_MODE,
    notificationMessage: 'Claude Code needs your approval for the plan',
    timestamp: minutesAgo(10),
  }),
})

// S5: Quiet Hopper — Non-interactive (amber border), TOOL_PERMISSION_REQUEST.
// Old timestamp (minutesAgo(30)) ensures it sorts last in nexus-web (tree uses 'newest' order).
export const SESSION_QUIET_HOPPER = buildMockSession({
  sessionId: SESSION_IDS.S5,
  sessionName: 'Quiet Hopper',
  hostName: 'Macbook Pro',
  host: HOST_MACBOOK_PRO,
  contextWorkDirectory: '/home/dev/projects/nexus-web',
  tmuxSessionName: null,
  dangerouslySkipPermissions: false,
  currentAgentMode: AgentMode.DEFAULT_MODE,
  isProcessing: true,
  latestEvent: buildMockEvent({
    eventType: EventType.TOOL_PERMISSION_REQUEST,
    agentMode: AgentMode.DEFAULT_MODE,
    notificationMessage: 'Claude needs your permission to use Bash',
    toolName: 'Bash',
    notificationContext: buildBashNotificationContext('npm run build'),
    timestamp: minutesAgo(30),
  }),
})

// ---------------------------------------------------------------------------
// Nexus Api — /home/dev/projects/nexus-api (2 sessions: S6–S7)
// ---------------------------------------------------------------------------

// S6: Refactor Auth Service — AGENT_REPLY
export const SESSION_REFACTOR_AUTH_SERVICE = buildMockSession({
  sessionId: SESSION_IDS.S6,
  sessionName: 'Refactor Auth Service',
  hostName: 'Macbook Pro',
  host: HOST_MACBOOK_PRO,
  contextWorkDirectory: '/home/dev/projects/nexus-api',
  tmuxSessionName: 'refactor-auth-service',
  dangerouslySkipPermissions: false,
  currentAgentMode: AgentMode.DEFAULT_MODE,
  isProcessing: false,
  latestEvent: buildMockEvent({
    eventType: EventType.AGENT_REPLY,
    agentMode: AgentMode.DEFAULT_MODE,
    agentResponse:
      "I've completed the refactoring of the AuthService class. The token validation logic is now extracted into a dedicated TokenValidator component",
    timestamp: minutesAgo(2),
  }),
})

// S7: Migrate Database Schema — FILE_MODIFICATION
export const SESSION_MIGRATE_DATABASE_SCHEMA = buildMockSession({
  sessionId: SESSION_IDS.S7,
  sessionName: 'Migrate Database Schema',
  hostName: 'Macbook Pro',
  host: HOST_MACBOOK_PRO,
  contextWorkDirectory: '/home/dev/projects/nexus-api',
  tmuxSessionName: 'migrate-database-schema',
  dangerouslySkipPermissions: false,
  currentAgentMode: AgentMode.DEFAULT_MODE,
  isProcessing: true,
  latestEvent: buildMockEvent({
    eventType: EventType.FILE_MODIFICATION,
    agentMode: AgentMode.DEFAULT_MODE,
    notificationMessage: 'File modified: V003__add_audit_columns.sql',
    toolName: 'Edit',
    notificationContext: buildToolNotificationContext('Edit'),
    timestamp: minutesAgo(8),
  }),
})

// ---------------------------------------------------------------------------
// Nexus Daemon — /home/dev/projects/nexus-daemon (2 sessions: S8–S9)
// ---------------------------------------------------------------------------

// S8: Optimize Encryption — AGENT_NARRATIVE
export const SESSION_OPTIMIZE_ENCRYPTION = buildMockSession({
  sessionId: SESSION_IDS.S8,
  sessionName: 'Optimize Encryption',
  hostName: 'Macbook Pro',
  host: HOST_MACBOOK_PRO,
  contextWorkDirectory: '/home/dev/projects/nexus-daemon',
  tmuxSessionName: 'optimize-encryption',
  dangerouslySkipPermissions: false,
  currentAgentMode: AgentMode.DEFAULT_MODE,
  isProcessing: true,
  latestEvent: buildMockEvent({
    eventType: EventType.AGENT_NARRATIVE,
    agentMode: AgentMode.DEFAULT_MODE,
    agentResponse:
      "I'll refactor the AES-GCM implementation to use the ring crate instead of openssl for better performance and smaller binary size",
    timestamp: minutesAgo(3),
  }),
})

// S9: Implement Key Rotation — AGENT_REPLY
export const SESSION_IMPLEMENT_KEY_ROTATION = buildMockSession({
  sessionId: SESSION_IDS.S9,
  sessionName: 'Implement Key Rotation',
  hostName: 'Macbook Pro',
  host: HOST_MACBOOK_PRO,
  contextWorkDirectory: '/home/dev/projects/nexus-daemon',
  tmuxSessionName: 'implement-key-rotation',
  dangerouslySkipPermissions: false,
  currentAgentMode: AgentMode.DEFAULT_MODE,
  isProcessing: false,
  latestEvent: buildMockEvent({
    eventType: EventType.AGENT_REPLY,
    agentMode: AgentMode.DEFAULT_MODE,
    agentResponse:
      'The key rotation mechanism is now implemented with automatic rollover support and backward compatibility',
    timestamp: minutesAgo(12),
  }),
})

// ===========================================================================
//  dev-server (locked, 3 sessions)
// ===========================================================================

// ---------------------------------------------------------------------------
// Nexus Api — /home/dev/projects/nexus-api (1 session: S10)
// ---------------------------------------------------------------------------

// S10: Setup API Monitoring — FILE_MODIFICATION
export const SESSION_SETUP_API_MONITORING = buildMockSession({
  sessionId: SESSION_IDS.S10,
  sessionName: 'Setup API Monitoring',
  hostName: 'Dev Server',
  host: HOST_DEV_SERVER,
  contextWorkDirectory: '/home/dev/projects/nexus-api',
  tmuxSessionName: 'setup-api-monitoring',
  dangerouslySkipPermissions: false,
  currentAgentMode: AgentMode.DEFAULT_MODE,
  isProcessing: true,
  latestEvent: buildMockEvent({
    eventType: EventType.FILE_MODIFICATION,
    agentMode: AgentMode.DEFAULT_MODE,
    notificationMessage: 'File modified: prometheus-config.yml',
    toolName: 'Write',
    notificationContext: buildToolNotificationContext('Write'),
    timestamp: minutesAgo(6),
  }),
})

// ---------------------------------------------------------------------------
// Nexus Infra — /home/dev/projects/nexus-infra (1 session: S11)
// ---------------------------------------------------------------------------

// S11: Update Helm Charts — AGENT_NARRATIVE
export const SESSION_UPDATE_HELM_CHARTS = buildMockSession({
  sessionId: SESSION_IDS.S11,
  sessionName: 'Update Helm Charts',
  hostName: 'Dev Server',
  host: HOST_DEV_SERVER,
  contextWorkDirectory: '/home/dev/projects/nexus-infra',
  tmuxSessionName: 'update-helm-charts',
  dangerouslySkipPermissions: false,
  currentAgentMode: AgentMode.DEFAULT_MODE,
  isProcessing: true,
  latestEvent: buildMockEvent({
    eventType: EventType.AGENT_NARRATIVE,
    agentMode: AgentMode.DEFAULT_MODE,
    agentResponse:
      "I'll update the Helm values to use the new container registry and adjust the resource limits",
    timestamp: minutesAgo(14),
  }),
})

// ---------------------------------------------------------------------------
// Nexus Daemon — /home/dev/projects/nexus-daemon (1 session: S12)
// ---------------------------------------------------------------------------

// S12: Benchmark Crypto — AGENT_REPLY
export const SESSION_BENCHMARK_CRYPTO = buildMockSession({
  sessionId: SESSION_IDS.S12,
  sessionName: 'Benchmark Crypto',
  hostName: 'Dev Server',
  host: HOST_DEV_SERVER,
  contextWorkDirectory: '/home/dev/projects/nexus-daemon',
  tmuxSessionName: 'benchmark-crypto',
  dangerouslySkipPermissions: false,
  currentAgentMode: AgentMode.DEFAULT_MODE,
  isProcessing: false,
  latestEvent: buildMockEvent({
    eventType: EventType.AGENT_REPLY,
    agentMode: AgentMode.DEFAULT_MODE,
    agentResponse:
      'Benchmarks complete. ring crate shows 2.3x throughput improvement over openssl for AES-256-GCM with 16KB payloads',
    timestamp: minutesAgo(18),
  }),
})

// ===========================================================================
//  ci-runner (locked, 2 sessions)
// ===========================================================================

// ---------------------------------------------------------------------------
// Nexus Infra — /home/dev/projects/nexus-infra (2 sessions: S13–S14)
// ---------------------------------------------------------------------------

// S13: Debug Pipeline Failure — TOOL_PERMISSION_REQUEST
export const SESSION_DEBUG_PIPELINE_FAILURE = buildMockSession({
  sessionId: SESSION_IDS.S13,
  sessionName: 'Debug Pipeline Failure',
  hostName: 'CI Runner',
  host: HOST_CI_RUNNER,
  contextWorkDirectory: '/home/dev/projects/nexus-infra',
  tmuxSessionName: 'debug-pipeline-failure',
  dangerouslySkipPermissions: false,
  currentAgentMode: AgentMode.DEFAULT_MODE,
  isProcessing: false,
  latestEvent: buildMockEvent({
    eventType: EventType.TOOL_PERMISSION_REQUEST,
    agentMode: AgentMode.DEFAULT_MODE,
    notificationMessage: 'Claude needs your permission to use Bash',
    toolName: 'Bash',
    notificationContext: buildBashNotificationContext(
      'kubectl logs -n staging deploy/nexus-api --tail=100',
    ),
    timestamp: minutesAgo(7),
  }),
})

// S14: Bright Dijkstra — Non-interactive, AGENT_REPLY
export const SESSION_BRIGHT_DIJKSTRA = buildMockSession({
  sessionId: SESSION_IDS.S14,
  sessionName: 'Bright Dijkstra',
  hostName: 'CI Runner',
  host: HOST_CI_RUNNER,
  contextWorkDirectory: '/home/dev/projects/nexus-infra',
  tmuxSessionName: null,
  dangerouslySkipPermissions: false,
  currentAgentMode: AgentMode.DEFAULT_MODE,
  isProcessing: false,
  latestEvent: buildMockEvent({
    eventType: EventType.AGENT_REPLY,
    agentMode: AgentMode.DEFAULT_MODE,
    agentResponse: 'Pipeline completed successfully. All 47 tests passed with 94% code coverage',
    timestamp: minutesAgo(22),
  }),
})

// ---------------------------------------------------------------------------
// Nexus Api — /home/dev/projects/nexus-api (1 session: S15)
// ---------------------------------------------------------------------------

// S15: Run Integration Tests — ERRORED_EVENT
export const SESSION_RUN_INTEGRATION_TESTS = buildMockSession({
  sessionId: SESSION_IDS.S15,
  sessionName: 'Run Integration Tests',
  hostName: 'CI Runner',
  host: HOST_CI_RUNNER,
  contextWorkDirectory: '/home/dev/projects/nexus-api',
  tmuxSessionName: 'run-integration-tests',
  dangerouslySkipPermissions: false,
  currentAgentMode: AgentMode.DEFAULT_MODE,
  isProcessing: false,
  latestEvent: buildMockEvent({
    eventType: EventType.ERRORED_EVENT,
    agentMode: AgentMode.DEFAULT_MODE,
    notificationContext: {
      toolName: 'Bash',
      toolInput: { error: 'Build failed: Cannot find module @nexus/auth-core' },
      agentDialogue: null,
    },
    timestamp: minutesAgo(25),
  }),
})

// S16: Acknowledged Bash (green variant) — for tile demos
// Same visual as FIX_RATE_LIMITER but with DEFAULT_MODE (green border, white diamond).
export const SESSION_ACKNOWLEDGED_BASH = buildMockSession({
  sessionId: SESSION_IDS.S16,
  sessionName: 'Fix Rate Limiter',
  hostName: 'Macbook Pro',
  host: HOST_MACBOOK_PRO,
  contextWorkDirectory: '/home/dev/projects/nexus-web',
  tmuxSessionName: 'fix-rate-limiter',
  dangerouslySkipPermissions: false,
  currentAgentMode: AgentMode.DEFAULT_MODE,
  isProcessing: false,
  latestEvent: buildMockEvent({
    eventType: EventType.TOOL_PERMISSION_REQUEST,
    agentMode: AgentMode.DEFAULT_MODE,
    notificationMessage: 'Claude wants to run: docker compose down && docker compose up -d --build',
    toolName: 'Bash',
    notificationContext: buildBashNotificationContext(
      'docker compose down && docker compose up -d --build',
    ),
    timestamp: minutesAgo(20),
  }),
})

// ---------------------------------------------------------------------------
// Acknowledged activity for Fix Rate Limiter (S3)
// ---------------------------------------------------------------------------

/**
 * Pre-built agent activity for Fix Rate Limiter showing an active Bash command.
 * Renders a blue (pre_tool) ActivityStatusIndicator dot with "Bashing" verb.
 */
export const FIX_RATE_LIMITER_ACTIVITY: FormattedAgentActivity = {
  toolName: 'Bash',
  activityContext: 'docker compose down && docker compose up -d --build',
  status: 'pre_tool',
  agentMode: AgentMode.BYPASS_PERMISSIONS_MODE,
}

/**
 * Pre-built agent activity for Migrate Database Schema showing an active Edit command.
 * Renders a blue (pre_tool) ActivityStatusIndicator dot with "Editing" verb.
 */
export const MIGRATE_DATABASE_ACTIVITY: FormattedAgentActivity = {
  toolName: 'Edit',
  activityContext: 'V003__add_audit_columns.sql',
  status: 'pre_tool',
  agentMode: AgentMode.DEFAULT_MODE,
}

/**
 * Pre-built agent activity for Optimize Encryption showing an active Read command.
 * Renders a blue (pre_tool) ActivityStatusIndicator dot with "Reading" verb.
 */
export const OPTIMIZE_ENCRYPTION_ACTIVITY: FormattedAgentActivity = {
  toolName: 'Read',
  activityContext: 'aes-gcm.rs',
  status: 'pre_tool',
  agentMode: AgentMode.DEFAULT_MODE,
}

export const WAITING_FOR_INPUT_ACTIVITY: FormattedAgentActivity = {
  toolName: '',
  activityContext: 'Claude is waiting for your input',
  status: 'system',
  agentMode: AgentMode.DEFAULT_MODE,
}

export const PROCESSING_REPLY_ACTIVITY: FormattedUserActivity = {
  toolName: '',
  activityContext: 'Processing your reply',
  status: 'user',
  agentMode: AgentMode.DEFAULT_MODE,
  isBlockingResponse: true,
}

/**
 * Pre-built userActivity for "Bash request acknowledged" with DEFAULT_MODE (green border).
 * Same visual as ACKNOWLEDGED_BASH_ACTIVITY but without BYPASS_PERMISSIONS_MODE.
 */
export const ACKNOWLEDGED_BASH_DEFAULT_ACTIVITY: FormattedUserActivity = {
  toolName: 'Bash',
  activityContext: 'docker compose down && docker compose up -d --build',
  status: 'user',
  agentMode: AgentMode.DEFAULT_MODE,
  isBlockingResponse: true,
  pendingAcknowledgement: {
    eventType: EventType.TOOL_PERMISSION_REQUEST,
    toolName: 'Bash',
  },
  isAcknowledged: true,
}

/**
 * Pre-built userActivity for the Fix Rate Limiter session's acknowledged Bash state.
 * When passed to MostRecentEventPreview, renders a green (non-pulsing) triangle icon
 * with "Bash request acknowledged" text.
 */
export const ACKNOWLEDGED_BASH_ACTIVITY: FormattedUserActivity = {
  toolName: 'Bash',
  activityContext: 'docker compose down && docker compose up -d --build',
  status: 'user',
  agentMode: AgentMode.BYPASS_PERMISSIONS_MODE,
  isBlockingResponse: true,
  pendingAcknowledgement: {
    eventType: EventType.TOOL_PERMISSION_REQUEST,
    toolName: 'Bash',
  },
  isAcknowledged: true,
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export const MOCK_SESSIONS: readonly ClaudeSession[] = [
  // macbook-pro: Nexus Web (5) — order controlled by timestamps
  SESSION_IMPLEMENT_OAUTH2,
  SESSION_ADD_FORM_VALIDATION,
  SESSION_OPTIMIZE_BUNDLE_SIZE,
  SESSION_QUIET_HOPPER,
  SESSION_FIX_RATE_LIMITER,
  // macbook-pro: Nexus Api (2)
  SESSION_REFACTOR_AUTH_SERVICE,
  SESSION_MIGRATE_DATABASE_SCHEMA,
  // macbook-pro: Nexus Daemon (2)
  SESSION_OPTIMIZE_ENCRYPTION,
  SESSION_IMPLEMENT_KEY_ROTATION,
  // dev-server sessions
  SESSION_SETUP_API_MONITORING,
  SESSION_UPDATE_HELM_CHARTS,
  SESSION_BENCHMARK_CRYPTO,
  // ci-runner sessions
  SESSION_DEBUG_PIPELINE_FAILURE,
  SESSION_BRIGHT_DIJKSTRA,
  SESSION_RUN_INTEGRATION_TESTS,
]

export const ANIMATED_SESSION_ID = SESSION_IMPLEMENT_OAUTH2.sessionId
